import { FC } from "react";
import { Outlet } from "react-router-dom";
import Header from "../Header/Header";
import Panel from "../Panel/Panel";

import "./Root.scss";
import { AccessRoles, PermissionRoles } from "../../models/models";
import { AccessRule, ROUTE_ACCESS } from "../../access";
import { useTheme } from "../../ThemeContext";
import { useAppSelector } from "../../assets/hooks/hooks";

// An item with no `access` is open to every authenticated user; a child without
// its own rule inherits the parent's.
export type PanelItemChildren = {
    name: string;
    path: string;
    access?: AccessRule;
};

export type PanelItems = {
    name: string;
    path: string;
    icon: string;
    children?: PanelItemChildren[];
    access?: AccessRule;
}[];

const defaultItemsPanel: PanelItems = [
    {
        name: "Key generator",
        path: "key-generator",
        icon: "key-generator",
        access: ROUTE_ACCESS.KEY_GENERATOR,
    },
    {
        // No rule of its own — the group shows up whenever at least one of its
        // children is accessible.
        name: "License keys",
        path: "license-keys",
        icon: "license-keys",
        children: [
            {
                name: "Actual",
                path: "actual",
                access: ROUTE_ACCESS.LICENSE_KEYS,
            },
            {
                name: "Migrated",
                path: "migrated",
                access: ROUTE_ACCESS.LICENSE_KEYS,
            },
            {
                name: "Not Activated",
                path: "not-activated",
                access: ROUTE_ACCESS.LICENSE_KEYS,
            },
            {
                name: "Iolo",
                path: "iolo",
                access: ROUTE_ACCESS.LICENSE_KEYS_IOLO,
            },
        ],
    },
    {
        name: "Products",
        path: "products",
        icon: "products",
        access: ROUTE_ACCESS.PRODUCTS,
        children: [
            {
                name: "Actual",
                path: "actual",
            },
            {
                name: "Migrated",
                path: "migrated",
            },
        ],
    },
    {
        name: "Projects",
        path: "projects",
        icon: "projects",
        access: ROUTE_ACCESS.PROJECTS,
    },
    {
        name: "Users",
        path: "users",
        icon: "users",
        access: ROUTE_ACCESS.USERS,
    },
    {
        name: "History",
        path: "history",
        icon: "history",
        access: ROUTE_ACCESS.HISTORY,
    },
    // {
    //     name: "Transactions",
    //     path: "transactions",
    //     icon: "transactions",
    // },
];

const supportItemsPanel: PanelItems = [
    {
        name: "License Keys",
        path: "license-keys/iolo",
        icon: "license-keys",
        access: ROUTE_ACCESS.LICENSE_KEYS_IOLO,
    },
    {
        name: "Products",
        path: "products",
        icon: "products",
        access: ROUTE_ACCESS.PRODUCTS,
        children: [
            { name: "Actual", path: "actual" },
            { name: "Migrated", path: "migrated" },
        ],
    },
    {
        name: "Projects",
        path: "projects",
        icon: "projects",
        access: ROUTE_ACCESS.PROJECTS,
    },
];

const Root: FC = () => {
    const { theme } = useTheme();
    const role = useAppSelector(
        (state) => state.initializeApp.role,
    ) as PermissionRoles | undefined;

    const items =
        role === AccessRoles.SUPPORT ? supportItemsPanel : defaultItemsPanel;

    return (
        <div className={"root_layout"} type-theme={theme}>
            <Header />

            <main className="main">
                <Panel items={items} />
                <div className="main__content">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default Root;
